import React, { useState } from "react";
import {
    Button,
    Toast,
    StandardModal,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalInput,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionTitle
} from "src/components";
import EmployeeDocumentUploader from "./Documents/EmployeeDocumentUploader";
import { EMPLOYEE_MODE_SOURCE } from "./Documents/utils";

import {
    adminUploadUserDocuments,
} from "src/components/Documents/api/documentsApi";

export function UserUploadsModal({
    open,
    onClose,
    user,
    onSaved,
}) {
    const [documents, setDocuments] = useState([]);
    const [saving, setSaving] = useState(false);

    const requiredDocuments = user?.requiredDocuments || [];

    const hasExistingDocs = requiredDocuments?.some(
        d => d?.upload?.url
    );

    const hasPendingUploads = documents.some(
        doc => doc && !doc?.uploaded
    );

    const handleClose = async () => {
        await onSaved?.();
        onClose?.();
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            await adminUploadUserDocuments({
                requiredDocuments,
                documents,
                employeeUuid: user.uuid,
            });

            Toast.success("Documents uploaded successfully.");

            onSaved?.();

            onClose?.();
        } catch (error) {
            console.error(error);
            Toast.error("Failed to upload documents.");
        } finally {
            setSaving(false);
        }
    };

    const shouldExpandModal =
        hasExistingDocs || hasPendingUploads;

    const userFields = [
        ["First Name", user?.firstName || ""],
        ["Last Name", user?.lastName || ""],
        ["Company Name", user?.customCompanyName || ""],
        ["Email", user?.email || ""],
        ["Mobile", user?.phoneNumber || ""],
        ["Referral Code", user?.referralCode || "-"],
    ];
    
    return (
        <StandardModal
            open={open}
            onClose={handleClose}
            title="User Documents"
            description="View and manage documents uploaded by this user. You can preview, download, and review each file below."
            widthMode="large"
            paperStyle={{
                minHeight: shouldExpandModal ? "min(840px, calc(100vh - 2rem))" : undefined
            }}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <Button
                        onClick={handleClose}
                        size="0.875rem"
                        border="1px solid #000"
                        color="#000"
                        width="8.75rem"
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                    >
                        Close
                    </Button>
                    {hasPendingUploads && (
                        <Button
                            backgroundColor="#171A22"
                            color="#fff"
                            onClick={handleSave}
                            disabled={saving}
                            width="140px"
                            borderRadius="7px"
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <StandardModalSectionTitle>User Details</StandardModalSectionTitle>
                <StandardModalFormGrid columns="repeat(3, minmax(0, 1fr))">
                    {userFields.map(([label, value]) => (
                        <StandardModalFormGroup key={label}>
                            <StandardModalLabel>{label}</StandardModalLabel>
                            <StandardModalInput value={value} readOnly />
                        </StandardModalFormGroup>
                    ))}
                </StandardModalFormGrid>
            </StandardModalSection>

            <StandardModalSection>
                <StandardModalSectionTitle>Documents Uploaded</StandardModalSectionTitle>
                <StandardModalDescription>
                    Review uploaded files or add new documents for this user.
                </StandardModalDescription>
                <EmployeeDocumentUploader
                    requiredDocuments={requiredDocuments}
                    title="Documents Uploaded"
                    mode={EMPLOYEE_MODE_SOURCE.USER_LIST}
                    color="#171A22"
                    onChange={({ documents }) => {
                        setDocuments(documents);
                    }}
                />
            </StandardModalSection>

        </StandardModal>
    );
}
